import { type ButtonPosition } from "@common/controls/Button/component";
import FormBlock from "@common/controls/Form/Block/component";
import CancelButton from "@common/controls/Form/Buttons/Cancel";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormControlled, type FormControlledRenderParams, type FormHandles } from "@common/controls/Form/component";
import { type WithSubmitProps } from "@common/controls/Modal/Modals/hocs/withSubmit";
import { DelayPreloader } from "@common/controls/Preloader/component";
import { type GenericMap } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { isUndefined } from "lodash";
import React from "react";
import { type DynamicFormMapOptions, type MapFormFieldInnerFn, getDefaultValues, isFieldVisible, mapFormElements } from "./lib";
import { type DynamicForms } from "./types";

export type DynamicFormPropsBase<T> = Partial<WithSubmitProps<GenericMap<any>>> & {
  cannotCancel?: boolean;
  className?: string;
  defaultValues?: GenericMap<any>;
  fields: DynamicForms.Field[];
  /**
   * undefined - no wrapping into a block
   * non-empty string - wrap into a form block
   */
  formBlockTitle?: string | null;
  isInline?: boolean;
  isLoading?: boolean;
  mapFormField?: MapFormFieldInnerFn;
  mapOptions?: DynamicFormMapOptions;
  onCancel?: () => void;
  onChange?: (name: keyof T, value: any) => void;
  isSubmitNoFieldsVisible?: boolean;
};

export type DynamicFormProps<T> = DynamicFormPropsBase<T> & {
  isSubmitButtonDisabled?: boolean;
  submitButtonPosition?: ButtonPosition;
  submitButtonCaption?: string;
};

type DynamicFormInnerProps<T> = DynamicFormPropsBase<T> & {
  renderSubmitButton: () => any;
  submitButtonLocation: "before" | "after";
};

type ContainerProps = {
  children: any;
  formBlockTitle?: string | null;
  isLoading?: boolean;
};
const Container = (props: ContainerProps) => {
  if (isUndefined(props.formBlockTitle)) return props.children;

  return (
    <FormBlock isLoading={props.isLoading} title={props.formBlockTitle}>
      {props.children}
    </FormBlock>
  );
};

export const DynamicFormInner = React.forwardRef(
  <T extends {}>(props: DynamicFormInnerProps<T>, ref: React.RefObject<FormHandles>) => {
    const i18n = useI18n();

    const defaultValues = React.useMemo(
      () => ({ ...getDefaultValues<T>(props.fields), ...props.defaultValues }),
      [props.defaultValues, props.fields]
    );
    const submit = React.useCallback(
      (parameters: GenericMap<any>) => {
        if (!props.submit) return;

        const values = props.fields
          .filter(
            (x) => x.formControlKey === "Hidden" || x.isHidden || isFieldVisible(x.visibilityCondition, parameters)
          )
          .reduce(
            (res, x) => ({
              ...res,
              [x.name]: parameters[x.name]
            }),
            {}
          );

        return props.submit(values);
      },
      [props.fields, props.submit]
    );

    const submitButton = props.renderSubmitButton();
    return (
      <FormControlled
        ref={ref}
        className={props.className}
        defaultValues={defaultValues as any}
        isInline={props.isInline}
        onCancel={props.onCancel}
        submit={submit}
        toast={props.toast}
      >
        {({ values }: FormControlledRenderParams<any>) => (
          <>
            {props.submitButtonLocation === "before" && submitButton}

            <Container formBlockTitle={props.formBlockTitle} isLoading={props.isLoading}>
              {props.isLoading ? (
                <DelayPreloader relative />
              ) : (
                mapFormElements(i18n, props.fields, values, props.mapOptions, props.mapFormField)
              )}
            </Container>

            {props.submitButtonLocation === "after" && !!submitButton && (
              <FormButtons>
                {!props.cannotCancel && <CancelButton />}
                {submitButton}
              </FormButtons>
            )}
          </>
        )}
      </FormControlled>
    );
  }
);

const DynamicForm = React.forwardRef(<T extends {}>(props: DynamicFormProps<T>, ref: React.RefObject<FormHandles>) => {
  const renderSubmitButton = React.useCallback(
    () =>
      !!props.fields.length || props.isSubmitNoFieldsVisible
        ? !!props.submit && (
            <SubmitButton
              isDisabled={props.isSubmitButtonDisabled}
              position={props.submitButtonPosition}
              title={props.submitButtonCaption}
            />
          )
        : null,
    [
      props.fields?.length,
      props.isSubmitButtonDisabled,
      props.isSubmitNoFieldsVisible,
      props.submit,
      props.submitButtonCaption,
      props.submitButtonPosition
    ]
  );

  return <DynamicFormInner {...props} ref={ref} submitButtonLocation="after" renderSubmitButton={renderSubmitButton} />;
});

export default DynamicForm;
